// Tiny media-query hook so React components can branch on the same
// breakpoints Tailwind v4 uses. Single source of truth means the JSX and
// the CSS never disagree about whether we're on desktop.
//
// Tailwind v4 default 'lg' = 1024px. We hard-code the same value here.

import { useEffect, useState } from 'react';

const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * Returns true when the viewport is at or above the given breakpoint.
 * SSR-safe (returns false until the first browser tick).
 */
export function useBreakpoint(bp: Breakpoint): boolean {
  const query = `(min-width: ${BREAKPOINTS[bp]}px)`;
  const get = () =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches;
  const [matches, setMatches] = useState<boolean>(get);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    setMatches(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

/** Convenience: true when the viewport is desktop-class (>= lg). */
export function useIsDesktop(): boolean {
  return useBreakpoint('lg');
}

/**
 * Returns true when the device's primary input is fine-grained (mouse).
 * Touch-primary devices (phones, tablets including iPad Pro landscape)
 * report `(pointer: coarse)`. Use this for surfaces that should only
 * appear on real desktops, regardless of viewport width — e.g. the
 * Demo plate overlay button, which is engineering scaffolding and
 * crowds the limited screen real estate on iPad even in landscape.
 *
 * SSR-safe: returns false until the first browser tick.
 */
export function useIsTouchPrimary(): boolean {
  const get = () =>
    typeof window === 'undefined'
      ? false
      : window.matchMedia('(pointer: coarse), (hover: none)').matches;
  const [matches, setMatches] = useState<boolean>(get);
  useEffect(() => {
    const mql = window.matchMedia('(pointer: coarse), (hover: none)');
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    setMatches(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return matches;
}
